/**
 * Holdings: what a province *becomes* when somebody spends a campaign on it.
 *
 * Taking ground was the only thing the strategic layer rewarded. A province had
 * one upgrade — walls — and one yield number that was fixed at generation and
 * never moved again, so the answer to "what do I do with the village I just
 * won" was *nothing*, and the answer to "what do I do next" was always "take
 * another one". This file is the other half of that sentence.
 *
 * Three ideas, and they only work together.
 *
 * **1. A tier ladder, ten rungs, bought from the world map.** Each rung pays
 * more gold, more levies and — from the fifth — materials. The steps compound at
 * fifteen per cent and cost thirty-eight per cent more each time, so the ladder
 * is long on purpose: a tenth-tier holding is the work of a hundred months and
 * several wars, which is the shape the whole game is aiming at.
 *
 * **2. Materials come out of a city, not out of a treasury.** Every province
 * sends what it produces to the nearest city it can reach *over its own roads*,
 * and every rung of the ladder is paid for out of the yard of the city that
 * supplies that province. Coin stays national — a letter of credit weighs
 * nothing — but timber does not travel, so a holding four weeks from the nearest
 * warehouse is four weeks of wagons dearer to build on. That is the whole reason
 * geography is in this file: the map decides what a thing costs.
 *
 * **3. The ladder changes what a holding *is*, not only what it pays.** Five of
 * the ten rungs do something a spreadsheet cannot express — a militia, a market,
 * a *charter* that turns the holding into a city of its own and puts a warehouse
 * on the frontier, roads that make its whole hinterland cheaper to build on, and
 * finally a citadel that raises the fortification ceiling past what any other
 * ground on the board may reach. Ten identical steps of "+5% yield" would have
 * been a slider with extra clicks.
 *
 * ── Why road distance, over owned ground ───────────────────────────────────
 *
 * "Closest city" is measured in `link.march` — the same months the campaign
 * moves armies in — along a path that never leaves the owner's own provinces.
 * Not straight-line distance, because the realm already has a graph and a
 * straight line across a mountain is a lie the map would be telling. Not
 * unrestricted road distance, because a wagon train does not cross a border it
 * is at war with. The consequence is the one worth having: a salient driven
 * through your realm does not merely take a province, it *cuts off* everything
 * behind it from the city that was building it, and a frontier province with no
 * connected city cannot be developed at all until you charter one out there.
 *
 * Nothing here is derived. The tier, the works, the cities and every yard of
 * goods in them are state: hashed by `hashCampaign`, written by `saveCampaign`,
 * and round-tripped.
 */

import {
  RESOURCE_KEYS,
  UNCAPPED_RESOURCES,
  type ResourceBundle,
  type ResourceKey,
} from '../types.ts';
import { provinceDef, type ProvinceTerrain } from '../../data/provinces.ts';
import { nationCapital, nationDef } from '../../data/nations.ts';
import { campYieldScale } from './bandits.ts';
import { TRADE_SHARE } from './tradetypes.ts';
import { linkBetween, neighbours } from './graph.ts';
import { logLine, provinceName } from './state.ts';
import { GARRISON_BASE, GARRISON_PER_FORT, type CampaignState, type CityState } from './types.ts';
import { standingGarrison } from './garrisons.ts';
import { manufactureCampaignGoods } from './industry.ts';
import { isVillageHolding, MAX_VILLAGE_LEVEL, villageLevel, villageLevelStats, villageProfile, villageTypeOf } from './villages.ts';

// ── The ladder ─────────────────────────────────────────────────────────────

export const MIN_TIER = 1;
export const MAX_TIER = 10;

/** What each rung is called. Index is `tier - 1`. */
export const TIER_NAMES: readonly string[] = [
  'Hamlet',
  'Steading',
  'Village',
  'Township',
  'Market Town',
  'Chartered Town',
  'Burgh',
  'City',
  'Great City',
  'Seat of the Realm',
];

/**
 * Multiplier on gold and on the province's strategic good, by tier.
 *
 * Fifteen per cent a rung, compounding: ×3.52 at the top. That is a large
 * number and it is meant to be — it is nine purchases, the last of which costs
 * more than a whole army — but it is a *multiplier on one province*, and one
 * province at tier ten is still worth less than four provinces at tier one. The
 * ladder is a reason to hold ground, not a substitute for holding it.
 */
export const TIER_YIELD: readonly number[] = [1, 1.15, 1.32, 1.52, 1.75, 2.01, 2.31, 2.66, 3.06, 3.52];

/**
 * Multiplier on levies, by tier. Ten per cent a rung rather than fifteen.
 *
 * Deliberately the shallower of the two. Manpower is the input to armies and
 * armies are what decide the map; a development ladder that doubled recruitment
 * as fast as it doubled income would make the tall crown unbeatable rather than
 * merely rich, and rich is the interesting one to be.
 */
export const TIER_LEVY: readonly number[] = [1, 1.1, 1.21, 1.33, 1.46, 1.61, 1.77, 1.95, 2.14, 2.36];

/** A rung that does something other than move a number. */
export interface TierStep {
  tier: number;
  name: string;
  text: string;
}

/**
 * The five rungs with texture on them, in order.
 *
 * Five of ten, not ten of ten, and the gaps are as deliberate as the steps: a
 * rung that quietly pays better is a fine rung to have between two that change
 * the holding, and a ladder where every step is an event has no events on it.
 */
export const TIER_STEPS: readonly TierStep[] = [
  {
    tier: 3,
    name: 'Militia',
    text: 'The village keeps its own watch: the garrison cap rises by 45 and refills half again as fast.',
  },
  {
    tier: 5,
    name: 'Market',
    text: 'A market day, and somewhere to sell — the tithe in kind rises by half again, the holding settles a third faster, and its city begins refining what it is sent.',
  },
  {
    tier: 6,
    name: 'Charter',
    text: 'A charter. The holding becomes a city in its own right: it keeps a yard, and everything around it is supplied from here.',
  },
  {
    tier: 8,
    name: 'Highways',
    text: 'Metalled roads out of the gates. Everything this city supplies is hauled at two fifths off.',
  },
  {
    tier: 10,
    name: 'Citadel',
    text: 'A citadel. Walls may be raised to 4 here — nowhere else on the board may — and the garrison cap rises by a further 120.',
  },
];

/** The step this tier grants, if it grants one. */
export function tierStep(tier: number): TierStep | undefined {
  return TIER_STEPS.find((s) => s.tier === tier);
}

function clampTier(tier: number): number {
  if (!Number.isFinite(tier)) return MIN_TIER;
  return Math.max(MIN_TIER, Math.min(MAX_TIER, Math.round(tier)));
}

export function tierOf(c: CampaignState, p: number): number {
  return isVillageHolding(c, p) ? villageLevel(c, p) : clampTier(c.provinces[p]?.tier ?? MIN_TIER);
}

export function tierName(tier: number): string {
  return TIER_NAMES[clampTier(tier) - 1];
}

export function tierYield(tier: number): number {
  return TIER_YIELD[clampTier(tier) - 1];
}

export function tierLevy(tier: number): number {
  return TIER_LEVY[clampTier(tier) - 1];
}

// ── What the steps actually do ─────────────────────────────────────────────

/** Militia, and then the citadel's own men. Added to `garrisonCap`. */
export function tierGarrison(tier: number): number {
  const t = clampTier(tier);
  return (t >= 3 ? 45 : 0) + (t >= 10 ? 120 : 0);
}

/** A militia refills faster than a levy does. */
export function tierRegrowth(tier: number): number {
  return clampTier(tier) >= 3 ? 1.5 : 1;
}

/** A market town has something to lose, and settles quicker for it. */
export function tierUnrestRelief(tier: number): number {
  return clampTier(tier) >= 5 ? 1.35 : 1;
}

/**
 * The fortification ceiling here.
 *
 * Three everywhere, four on a citadel. This is the one place on the board where
 * a crown can put up something the map does not otherwise allow, which is what
 * makes the top of the ladder worth reaching for a defensive player rather than
 * only for an accountant.
 */
export const MAX_FORT_BASE = 3;
export const MAX_FORT_CITADEL = 4;

export function maxFortAt(c: CampaignState, p: number): number {
  if (isVillageHolding(c, p)) return Math.max(2, c.provinces[p].fort);
  return tierOf(c, p) >= 10 ? MAX_FORT_CITADEL : MAX_FORT_BASE;
}

/** Garrison cap: the walls, and whatever the town has built for itself. */
export function garrisonCapAt(c: CampaignState, p: number): number {
  return (
    GARRISON_BASE +
    (c.provinces[p]?.fort ?? 0) * GARRISON_PER_FORT +
    tierGarrison(tierOf(c, p)) +
    // The place's own watch. This and `garrisonCap` in `state.ts` are the two
    // existing spellings of the same sum and they must not drift.
    standingGarrison(c, p)
  );
}

// ── The tithe in kind ──────────────────────────────────────────────────────

/**
 * The good the country itself gives up, by the ground it stands on.
 *
 * A province's `resource` in the data table is its *strategic* good and thirty of
 * the hundred and twenty-seven have none at all. This is different: it is what
 * the land around a holding produces whether or not anybody wrote it down, so
 * every terrain has one and every province on the board pays it. It is both the
 * thing that makes a developed holding worth keeping rather than merely worth
 * taking, and — see `localGood` and the substitution rule below — the guarantee
 * that no crown can be locked out of the ladder by the accident of its terrain.
 */
const TERRAIN_TITHE: Record<ProvinceTerrain, ResourceKey> = {
  plain: 'grain',
  hill: 'iron',
  forest: 'timber',
  marsh: 'grain',
  coast: 'grain',
  mountain: 'stone',
  ruin: 'stone',
  // The three `worldshape` added. A jungle is timber and little else you can
  // carry home; a desert is what the rock gives up; ice is stone under snow.
  jungle: 'timber',
  desert: 'stone',
  ice: 'stone',
};

/** What the country around this province gives up. Every province has one. */
export function localGood(p: number): ResourceKey {
  return TERRAIN_TITHE[provinceDef(p).terrain];
}

/**
 * Units a month at the first rung, per rung above it, and what a market adds.
 *
 * The tithe used to begin at the fifth rung, and that was very nearly a fatal
 * bug rather than a balance choice: a crown whose ground grows no timber had no
 * way to *get* timber, the second rung costs forty of it, and thirteen of the
 * twenty-four crowns on this board — the player's included — were therefore
 * locked out of the entire ladder from the first month, permanently. Goods now
 * flow from the first rung. The market is still felt, but as a step up in rate
 * rather than as the difference between something and nothing.
 */
export const TITHE_BASE = 0.5;
export const TITHE_PER_TIER = 0.55;
export const TITHE_MARKET = 1.6;

/** What this province tithes in kind this month, before unrest and brigands. */
export function titheOf(
  c: CampaignState,
  p: number,
  atTier?: number,
): { key: ResourceKey; amount: number } {
  const tier = atTier === undefined ? tierOf(c, p) : clampTier(atTier);
  const market = tier >= 5 ? TITHE_MARKET : 1;
  return {
    key: localGood(p),
    amount: (TITHE_BASE + TITHE_PER_TIER * (tier - 1)) * market,
  };
}

/**
 * The multiplier every yield of a province shares: how developed it is, how
 * restive, whether somebody is sitting outside the gate, and whether brigands
 * are taking a cut.
 *
 * One function because there were three copies of it — `doIncome`,
 * `projectedIncome` and `crownYield` — and a fourth was about to be written for
 * the tier ladder. Copies of a formula drift; this one is now the formula.
 */
export function yieldScale(c: CampaignState, p: number, atTier?: number): number {
  const prov = c.provinces[p];
  if (!prov) return 0;
  const tier = atTier === undefined ? tierOf(c, p) : clampTier(atTier);
  return (
    prov.development *
    (1 - prov.unrest) *
    (prov.siegeBy >= 0 ? 0.3 : 1) *
    campYieldScale(c, p) *
    (isVillageHolding(c, p) ? villageLevelStats(villageTypeOf(c, p)!, tier).yieldMultiplier : tierYield(tier))
  );
}

/** What a holding pays its crown in a month, as things currently stand. */
export interface HoldingIncome {
  /**
   * Coin into the treasury: the **tax half only**, already net of
   * `TRADE_SHARE`. This is what the crown actually collects from this ground.
   */
  gold: number;
  /**
   * The other third, which goes to the province's trade node and comes back
   * only through control of it — possibly somebody else's control.
   *
   * Reported separately rather than folded into `gold` or left out, because
   * "this village pays you 7 and sends 4 more to a market you do not hold" is
   * the sentence a player needs, and neither number alone tells it. The tier
   * ladder lifts both halves; see `provinceTradeValue`.
   */
  trade: number;
  levy: number;
  goods: Array<{ key: ResourceKey; amount: number }>;
}

/**
 * What a holding pays this month.
 *
 * `atTier` asks the counterfactual — *what would it pay a rung higher* — which
 * the world map needs to price the next step. It is a parameter rather than a
 * temporary write to `prov.tier` because a read that mutates the world, even one
 * that tidies up after itself, is a read that can be interrupted, and this one is
 * called from the UI while the sim is running.
 */
export function holdingIncome(c: CampaignState, p: number, atTier?: number): HoldingIncome {
  const def = provinceDef(p);
  const scale = yieldScale(c, p, atTier);
  const prov = c.provinces[p];
  const tier = atTier === undefined ? tierOf(c, p) : clampTier(atTier);
  const levyScale =
    prov.development *
    (1 - prov.unrest) *
    (prov.siegeBy >= 0 ? 0.3 : 1) *
    campYieldScale(c, p) *
    tierLevy(tier);

  const goods: Array<{ key: ResourceKey; amount: number }> = [];
  const profile = villageProfile(c, p);
  if (profile) goods.push({ key: profile.primaryResource, amount: (8 + Math.max(4, def.yield)) * scale });
  else if (def.resource) goods.push({ key: def.resource as ResourceKey, amount: def.yield * scale });
  const tithe = titheOf(c, p, atTier);
  const hit = goods.find((g) => g.key === tithe.key);
  if (hit) hit.amount += tithe.amount * scale;
  else goods.push({ key: tithe.key, amount: tithe.amount * scale });

  /**
   * The split, applied here and nowhere else.
   *
   * `TRADE_SHARE` of a province's wealth is trade rather than tax. Trade landed
   * on master while this branch was in flight, and it wrote the split inline in
   * `doIncome` — which was correct at the time and would have been quietly wrong
   * the moment this file took `doIncome` over, because then the panel and the
   * month would each have had their own idea of what a province pays. It lives
   * in the one reader now, and `provinceTradeValue` takes the complement off the
   * same `yieldScale`.
   */
  const wealth = def.wealth * scale;
  return {
    gold: wealth * (1 - TRADE_SHARE),
    trade: wealth * TRADE_SHARE,
    levy: def.levy * levyScale,
    goods,
  };
}

// ── Cities ─────────────────────────────────────────────────────────────────

/**
 * How much of one good a city's yard will hold, by the tier of the province it
 * stands on.
 *
 * The cap is the second reason to build a city up rather than only outward. A
 * hamlet with a warehouse can hold three hundred timber, which is enough for the
 * fourth rung of a ladder and nowhere near enough for the ninth; the goods for a
 * great city have to be stored somewhere before they can be spent, so the crown
 * that wants a tall frontier has to build the city behind it first.
 */
export const CITY_STOCK_BASE = 320;
export const CITY_STOCK_PER_TIER = 0.7;

export function cityStockCap(c: CampaignState, at: number): number {
  return Math.round(CITY_STOCK_BASE * (1 + CITY_STOCK_PER_TIER * (tierOf(c, at) - 1)));
}

/** The tier at which a holding is chartered and becomes a city of its own. */
export const CHARTER_TIER = 6;
/** The tier at which a city's roads make its whole hinterland cheaper. */
export const HIGHWAY_TIER = 8;

export function cityAt(c: CampaignState, p: number): CityState | undefined {
  if (isVillageHolding(c, p)) return undefined;
  return c.cities.find((k) => k.at === p);
}

export function isCity(c: CampaignState, p: number): boolean {
  return cityAt(c, p) !== undefined;
}

/** Cities this crown currently holds, in province order. */
export function citiesOf(c: CampaignState, nation: number): CityState[] {
  return c.cities.filter((k) => c.provinces[k.at]?.owner === nation && !isVillageHolding(c, k.at));
}

function emptyStock(): ResourceBundle {
  const out = {} as ResourceBundle;
  for (const k of RESOURCE_KEYS) out[k] = 0;
  return out;
}

/**
 * Put a warehouse on a province. Idempotent: chartering a city twice is a
 * no-op rather than a second yard, and `cities` stays sorted by province so
 * every walk of it — the hash, the save, the draw of materials — is stable.
 */
export function charterCity(c: CampaignState, at: number, quiet = false): CityState | undefined {
  if (isVillageHolding(c, at)) return undefined;
  const already = cityAt(c, at);
  if (already) return already;
  const city: CityState = {
    at,
    owner: c.provinces[at]?.owner ?? -1,
    stock: emptyStock(),
    founded: c.turn,
  };
  c.cities.push(city);
  c.cities.sort((a, b) => a.at - b.at);
  if (!quiet) {
    logLine(c, `${provinceName(at)} is chartered. Its yard is open to the country around it.`);
  }
  return city;
}

// ── Supply: which city feeds which province ────────────────────────────────

/** Every province's nearest city, and how many months of road away it is. */
export interface SupplyMap {
  /** Province index of the supplying city, or -1 where none can be reached. */
  city: number[];
  /** Months of road to it. `Infinity` where there is none. */
  dist: number[];
}

/**
 * Multi-source shortest path from all of a crown's cities at once, over its own
 * ground only.
 *
 * One pass per crown per month rather than one per province, which is the whole
 * reason it is written this way: the answer for *every* province falls out of a
 * single search. A hundred and twenty-seven nodes make the naive selection loop
 * cheaper than a heap and considerably easier to be sure of.
 *
 * Ties break toward the lower province index, so which of two equidistant cities
 * feeds a village is a fact about the board rather than about iteration luck.
 */
export function supplyMap(c: CampaignState, nation: number): SupplyMap {
  const n = c.provinces.length;
  const dist = new Array<number>(n).fill(Number.POSITIVE_INFINITY);
  const city = new Array<number>(n).fill(-1);
  const done = new Array<boolean>(n).fill(false);

  for (const k of c.cities) {
    if (isVillageHolding(c, k.at)) continue;
    if (c.provinces[k.at]?.owner !== nation) continue;
    dist[k.at] = 0;
    city[k.at] = k.at;
  }

  for (;;) {
    let best = -1;
    let bestD = Number.POSITIVE_INFINITY;
    for (let i = 0; i < n; i++) {
      if (done[i] || dist[i] >= bestD) continue;
      bestD = dist[i];
      best = i;
    }
    if (best < 0) break;
    done[best] = true;
    for (const nb of neighbours(c, best)) {
      if (c.provinces[nb].owner !== nation) continue;
      const link = linkBetween(c, best, nb);
      const step = bestD + (link ? link.march : 1);
      if (step < dist[nb] || (step === dist[nb] && city[best] < city[nb])) {
        dist[nb] = step;
        city[nb] = city[best];
      }
    }
  }
  return { city, dist };
}

/** Months of road from one province to every other, over this crown's ground. */
function roadsFrom(c: CampaignState, nation: number, from: number): number[] {
  const n = c.provinces.length;
  const dist = new Array<number>(n).fill(Number.POSITIVE_INFINITY);
  const done = new Array<boolean>(n).fill(false);
  if (from < 0 || from >= n) return dist;
  dist[from] = 0;
  for (;;) {
    let best = -1;
    let bestD = Number.POSITIVE_INFINITY;
    for (let i = 0; i < n; i++) {
      if (done[i] || dist[i] >= bestD) continue;
      bestD = dist[i];
      best = i;
    }
    if (best < 0) break;
    done[best] = true;
    for (const nb of neighbours(c, best)) {
      if (c.provinces[nb].owner !== nation) continue;
      const link = linkBetween(c, best, nb);
      const step = bestD + (link ? link.march : 1);
      if (step < dist[nb]) dist[nb] = step;
    }
  }
  return dist;
}

/** Where this province's goods go, and what a wagon train from there costs. */
export interface SupplyRoute {
  /** Province index of the city, or -1. */
  city: number;
  /** Months of road. 0 when the province is its own city. */
  dist: number;
}

export function supplyRouteOf(c: CampaignState, p: number): SupplyRoute {
  const owner = c.provinces[p]?.owner ?? -1;
  if (owner < 0) return { city: -1, dist: Number.POSITIVE_INFINITY };
  const map = supplyMap(c, owner);
  return { city: map.city[p], dist: map.dist[p] };
}

/**
 * What the haul does to the price of materials.
 *
 * Coin is untouched: gold does not care how far it has to go. Everything with a
 * weight is multiplied, which is the number that makes a far-flung province
 * expensive to develop and a chartered frontier city worth its enormous price.
 */
export const HAUL_PER_MONTH = 0.11;
export const HAUL_MAX = 2.4;
/** Metalled roads out of a great city's gates take this much off the haul. */
export const HIGHWAY_RELIEF = 0.6;

export function haulMultiplier(c: CampaignState, route: SupplyRoute): number {
  if (route.city < 0 || !Number.isFinite(route.dist)) return HAUL_MAX;
  const relief = tierOf(c, route.city) >= HIGHWAY_TIER ? HIGHWAY_RELIEF : 1;
  return Math.min(HAUL_MAX, 1 + HAUL_PER_MONTH * route.dist * relief);
}

// ── What a rung costs ──────────────────────────────────────────────────────

/** Coin for the first rung, and how much dearer each rung is than the last. */
export const DEV_GOLD_BASE = 110;
export const DEV_GROWTH = 1.38;

/**
 * The materials, and the rung each one starts being needed at.
 *
 * A steading wants timber. A village wants stone as well. Iron arrives with the
 * township, grain with the market that has to feed the men building it, and the
 * three refined goods with the top of the ladder — which is what makes a city's
 * own industry (see `refineCities`) a prerequisite for a great city rather than
 * a curiosity.
 */
const DEV_GOODS: ReadonlyArray<{ key: ResourceKey; base: number; from: number }> = [
  { key: 'timber', base: 40, from: 2 },
  { key: 'stone', base: 34, from: 3 },
  { key: 'iron', base: 18, from: 4 },
  { key: 'grain', base: 26, from: 5 },
  { key: 'planks', base: 10, from: 7 },
  { key: 'runestone', base: 9, from: 8 },
  { key: 'ingots', base: 8, from: 9 },
];

/**
 * One line of the bill.
 *
 * `insteadOf` is set when this is the province's own country standing in for a
 * good the region does not produce — see `SUBSTITUTE_PENALTY`. The world map
 * should say so on the line, because "64 iron" and "64 iron in place of timber"
 * are different sentences and only one of them explains the price.
 */
export interface DevelopLine {
  key: ResourceKey;
  amount: number;
  insteadOf?: ResourceKey;
}

/**
 * What it costs to build with the wrong thing.
 *
 * The ladder asks for timber, then stone, then iron, then grain, then the
 * refined goods — a mix, on purpose, so that a great city is a thing a whole
 * realm is spent on rather than one province's output banked. But thirteen of
 * the twenty-four crowns on this board hold no forest and no timber province,
 * and a requirement a crown *cannot ever satisfy* is not difficulty, it is a
 * locked door. So a shortfall is made up out of the country's own good at a
 * penalty: a hill crown raises its first steading out of sixty-four iron
 * instead of forty timber, and pays through the nose for the privilege.
 *
 * The mix still matters — everything about having the right materials is
 * cheaper — and geography still decides, but it decides a *price* rather than a
 * permission. That is the difference between a constraint and a wall.
 */
export const SUBSTITUTE_PENALTY = 1.6;

export interface DevelopCost {
  from: number;
  to: number;
  /** Out of the crown's treasury. */
  gold: number;
  /**
   * Out of the supplying city's yard, haul and any substitution included. This
   * is the bill as it would actually be paid against the yards *right now*.
   */
  goods: DevelopLine[];
  /** What the rung asks for before substitution, for a panel that wants to explain. */
  wants: Array<{ key: ResourceKey; amount: number }>;
  /** Months of building before the tier lands. */
  months: number;
  /** The supplying city and the road to it. */
  route: SupplyRoute;
  haulMul: number;
}

/** Months a rung takes to build. Two at the bottom of the ladder, six at the top. */
export function developMonths(to: number): number {
  return 1 + Math.ceil(to / 2);
}

/**
 * What it would take to raise this province one rung, or null if it is already
 * at the top.
 *
 * Reads the yards but spends nothing: the bill depends on what is in them,
 * because what is missing is what gets substituted. `developBlock` is the one
 * that says no.
 */
export function developCost(c: CampaignState, p: number): DevelopCost | null {
  const from = tierOf(c, p);
  if (from >= (isVillageHolding(c, p) ? MAX_VILLAGE_LEVEL : MAX_TIER)) return null;
  const to = from + 1;
  const growth = Math.pow(DEV_GROWTH, to - 2);
  const route = supplyRouteOf(c, p);
  const haulMul = haulMultiplier(c, route);
  const wants = DEV_GOODS.filter((g) => to >= g.from).map((g) => ({
    key: g.key,
    amount: Math.round(g.base * growth * haulMul),
  }));
  const goods = substitute(c, p, wants);
  return {
    from,
    to,
    gold: Math.round(DEV_GOLD_BASE * growth),
    goods,
    wants,
    months: developMonths(to),
    route,
    haulMul,
  };
}

/**
 * Turn what a rung asks for into what would actually be paid, given what is in
 * the yards this province can reach.
 *
 * Each good is covered as far as the yards can cover it; the remainder is
 * converted to the province's own country good at `SUBSTITUTE_PENALTY`. A
 * province whose country good *is* the missing good has nothing to substitute
 * with and simply comes up short, which is correct — a forest that has run out
 * of timber waits for the timber.
 *
 * The result may still be unaffordable. That is `shortfall`'s job to notice, and
 * it has to be, because two different goods can both substitute into the same
 * one and the yards only have what they have.
 */
function substitute(
  c: CampaignState,
  p: number,
  wants: ReadonlyArray<{ key: ResourceKey; amount: number }>,
): DevelopLine[] {
  const owner = c.provinces[p]?.owner ?? -1;
  const yards = owner >= 0 ? drawOrder(c, owner, p) : [];
  const local = localGood(p);

  const out: DevelopLine[] = [];
  for (const want of wants) {
    let have = 0;
    for (const k of yards) have += k.stock[want.key];
    const direct = Math.min(have, want.amount);
    if (direct > 0.001) out.push({ key: want.key, amount: direct });
    const rest = want.amount - direct;
    if (rest <= 0.001) continue;
    if (want.key === local) {
      // Nothing to stand in for it. Ask for the whole amount and come up short.
      if (direct > 0.001) out[out.length - 1].amount = want.amount;
      else out.push({ key: want.key, amount: want.amount });
      continue;
    }
    out.push({
      key: local,
      amount: Math.round(rest * SUBSTITUTE_PENALTY),
      insteadOf: want.key,
    });
  }
  return out;
}

/** The bill merged one line per resource, which is how it is actually paid. */
function merged(goods: ReadonlyArray<DevelopLine>): Array<{ key: ResourceKey; amount: number }> {
  const out: Array<{ key: ResourceKey; amount: number }> = [];
  for (const g of goods) {
    const hit = out.find((x) => x.key === g.key);
    if (hit) hit.amount += g.amount;
    else out.push({ key: g.key, amount: g.amount });
  }
  return out;
}

// ── Walls, on the same footing ─────────────────────────────────────────────

/**
 * Fortification, priced the same way a rung is.
 *
 * The walls were the map's *other* upgrade and they were paid for out of the
 * national stone pile — which was fine while all stone was national, and became
 * incoherent the moment stone started arriving in a particular warehouse. It
 * would also have been a straightforward regression: the crown's quarries now
 * fill a yard, so a player who could once fortify in the second month would have
 * been waiting on a yard to overflow before the treasury saw any stone at all.
 *
 * So walls come out of the yard, hauled, substituted and refused in a sentence,
 * exactly as a rung does. A frontier fort is dear for the same reason a frontier
 * road is dear, and the two upgrade axes finally speak the same language.
 *
 * The cost grows with the level where it used to be flat, which it has to: the
 * fourth level exists only on a citadel and should be the most expensive thing a
 * province can buy, not the same price as its first palisade.
 */
export const FORTIFY_GOLD = 90;
export const FORTIFY_STONE = 70;
export const FORTIFY_GROWTH = 1.5;

export interface FortifyCost {
  /** The level being built. */
  to: number;
  gold: number;
  goods: DevelopLine[];
  wants: Array<{ key: ResourceKey; amount: number }>;
  route: SupplyRoute;
  haulMul: number;
}

/** What raising these walls one level would take, or null if they are at the cap. */
export function fortifyCost(c: CampaignState, p: number): FortifyCost | null {
  const prov = c.provinces[p];
  if (!prov) return null;
  const to = prov.fort + 1;
  if (to > maxFortAt(c, p)) return null;
  const growth = Math.pow(FORTIFY_GROWTH, to - 1);
  const route = supplyRouteOf(c, p);
  const haulMul = haulMultiplier(c, route);
  const wants = [{ key: 'stone' as ResourceKey, amount: Math.round(FORTIFY_STONE * growth * haulMul) }];
  return {
    to,
    gold: Math.round(FORTIFY_GOLD * growth),
    goods: substitute(c, p, wants),
    wants,
    route,
    haulMul,
  };
}

/** Why these walls may not go higher — a sentence, or '' when they may. */
export function fortifyBlock(c: CampaignState, p: number, nation: number): string {
  const prov = c.provinces[p];
  if (!prov) return 'There is no such ground.';
  if (prov.owner !== nation) return 'This is not your ground.';
  if (isVillageHolding(c, p)) return 'Upgrade the village to improve its preset defenses.';
  if (prov.siegeBy >= 0) return 'There is a host outside the gate.';
  const cost = fortifyCost(c, p);
  if (!cost) {
    return prov.fort >= MAX_FORT_CITADEL
      ? 'These walls are as high as walls go.'
      : `Only a citadel may raise walls above ${MAX_FORT_BASE}.`;
  }
  if (cost.route.city < 0) return 'No city of yours can reach it to supply the works.';
  if (c.nations[nation].treasury.gold < cost.gold) {
    return `The treasury is ${Math.ceil(cost.gold - c.nations[nation].treasury.gold)} gold short.`;
  }
  const short = shortfall(c, nation, p, cost.goods);
  if (short) {
    return `${provinceName(cost.route.city)} and everything behind it are ${short.amount} ${short.key} short.`;
  }
  return '';
}

/** Raise the walls one level, out of the coin and the yard. */
export function raiseWalls(c: CampaignState, p: number, nation: number): boolean {
  if (fortifyBlock(c, p, nation) !== '') return false;
  const cost = fortifyCost(c, p);
  if (!cost) return false;
  c.nations[nation].treasury.gold -= cost.gold;
  drawGoods(c, nation, p, cost.goods);
  c.provinces[p].fort = cost.to;
  return true;
}

/** Holdings one crown may have masons on at once. */
export const MAX_WORKS_PER_NATION = 2;

function worksCount(c: CampaignState, nation: number): number {
  let n = 0;
  for (const prov of c.provinces) if (prov.worksTo > 0 && prov.worksBy === nation) n++;
  return n;
}

/** Unrest above which nobody will start a building project. */
export const DEV_UNREST_CEILING = 0.6;

/**
 * Why this crown may not raise this holding a rung — a sentence, or '' when it
 * may.
 *
 * A sentence rather than a boolean because the world map is expected to grey the
 * button and say why; a refusal the player cannot read is indistinguishable from
 * a bug, and this system has more ways to refuse than any other on the board.
 */
export function developBlock(c: CampaignState, p: number, nation: number): string {
  const prov = c.provinces[p];
  if (!prov) return 'There is no such ground.';
  if (prov.owner !== nation) return 'This is not your ground.';
  if (isVillageHolding(c, p) && tierOf(c, p) >= MAX_VILLAGE_LEVEL) return 'This village has reached level 7.';
  if (tierOf(c, p) >= MAX_TIER) return 'There is nothing above a Seat of the Realm.';
  if (prov.worksTo > 0) return 'The masons are already at work here.';
  if (prov.siegeBy >= 0) return 'There is a host outside the gate.';
  if (prov.unrest > DEV_UNREST_CEILING) return 'The country is too restive to build in.';
  if (worksCount(c, nation) >= MAX_WORKS_PER_NATION) {
    return `Your masons are already on ${MAX_WORKS_PER_NATION} other holdings.`;
  }

  const cost = developCost(c, p);
  if (!cost) return 'There is nothing above a Seat of the Realm.';
  if (cost.route.city < 0) {
    return 'No owned city can supply it. Open the road or take a nearby city.';
  }
  if (c.nations[nation].treasury.gold < cost.gold) {
    return `The treasury is ${Math.ceil(cost.gold - c.nations[nation].treasury.gold)} gold short.`;
  }
  const short = shortfall(c, nation, p, cost.goods);
  if (short) {
    return `${provinceName(cost.route.city)} and everything behind it are ${short.amount} ${short.key} short.`;
  }
  return '';
}

/**
 * The cities a draw for this province walks, nearest first.
 *
 * The city that *supplies* the province is the one named on the panel and the
 * one the haul is priced off, and it is drawn from first. What is missing comes
 * from the next city out, and the next — because a ladder that could deadlock on
 * a hill province that will never see a plank is a ladder nobody climbs. The
 * price was already paid at the door: the haul multiplier is a function of the
 * nearest city, so a crown whose nearest warehouse is empty is paying a cheap
 * haul for goods that came a long way, and a crown with nothing anywhere still
 * cannot build.
 */
export function drawOrder(c: CampaignState, nation: number, p: number): CityState[] {
  const roads = roadsFrom(c, nation, p);
  const primary = supplyRouteOf(c, p).city;
  return citiesOf(c, nation)
    .filter((k) => k.at === primary || Number.isFinite(roads[k.at]))
    .sort((a, b) => {
      if (a.at === primary) return -1;
      if (b.at === primary) return 1;
      return roads[a.at] - roads[b.at] || a.at - b.at;
    });
}

/**
 * The first good the yards cannot cover, merged — because two lines of the bill
 * can name the same resource once a substitution has happened, and a check that
 * looked at them one at a time would pass a bill that cannot be paid.
 */
function shortfall(
  c: CampaignState,
  nation: number,
  p: number,
  goods: ReadonlyArray<DevelopLine>,
): { key: ResourceKey; amount: number } | null {
  const yards = drawOrder(c, nation, p);
  for (const g of merged(goods)) {
    let have = 0;
    for (const k of yards) have += k.stock[g.key];
    if (have < g.amount - 0.001) return { key: g.key, amount: Math.ceil(g.amount - have) };
  }
  return null;
}

/** Take the goods out of the yards, nearest first. Assumes they are there. */
function drawGoods(
  c: CampaignState,
  nation: number,
  p: number,
  goods: ReadonlyArray<DevelopLine>,
): void {
  const yards = drawOrder(c, nation, p);
  for (const g of merged(goods)) {
    let owed = g.amount;
    for (const k of yards) {
      if (owed <= 0) break;
      const take = Math.min(owed, k.stock[g.key]);
      k.stock[g.key] -= take;
      owed -= take;
    }
  }
}

// ── Ordering, and cancelling, the works ────────────────────────────────────

/**
 * Begin building the next rung. Returns false — silently, like everything else
 * in this layer — when it may not go ahead.
 *
 * The materials leave the yard *now* and the tier lands months later. That is
 * the whole reason works are state rather than a purchase: a crown that has
 * committed its timber to a road in the back country has committed it, and a
 * province that changes hands in the meantime takes the half-built works with
 * it.
 */
export function beginWorks(c: CampaignState, p: number, nation: number): boolean {
  if (developBlock(c, p, nation) !== '') return false;
  const cost = developCost(c, p);
  if (!cost) return false;

  c.nations[nation].treasury.gold -= cost.gold;
  drawGoods(c, nation, p, cost.goods);

  const prov = c.provinces[p];
  prov.worksTo = cost.to;
  prov.worksT = cost.months;
  prov.worksBy = nation;
  return true;
}

/** How much of the coin comes back when the masons are sent home. */
export const CANCEL_REFUND = 0.5;

/**
 * Call the masons off. Half the coin comes back; the materials do not.
 *
 * The draft refunded half the goods too, by recomputing `developCost` at the
 * moment of cancelling — and once the bill became a function of what is in the
 * yards, that stopped being the bill that was actually paid. It would have
 * refunded timber to a crown that had paid in iron. Rather than carry a paid
 * bill on every province for the sake of a refund, the rule is now the simpler
 * and more honest one: the stone is in the ground, only the unspent wages come
 * home. It also makes ordering works a decision rather than a thing to toggle.
 */
export function cancelWorks(c: CampaignState, p: number, nation: number): boolean {
  const prov = c.provinces[p];
  if (!prov || prov.worksTo <= 0 || prov.worksBy !== nation) return false;
  const cost = developCost(c, p);
  prov.worksTo = 0;
  prov.worksT = 0;
  prov.worksBy = -1;
  if (cost) c.nations[nation].treasury.gold += Math.round(cost.gold * CANCEL_REFUND);
  return true;
}

// ── Goods coming in ────────────────────────────────────────────────────────

/**
 * Put a province's production into the yard of the city that supplies it, and
 * spill whatever will not fit into the treasury.
 *
 * The spill is what keeps this change from quietly starving the rest of the
 * campaign. Materials used to go straight into the national purse and armies are
 * still raised out of it; if the yards simply swallowed everything, iron would
 * stop existing at national scale the month this shipped. So a full yard sends
 * the surplus on — which is also the right fiction, because a warehouse with
 * nowhere to put the timber sells it.
 */
export function depositGoods(
  c: CampaignState,
  supply: SupplyMap,
  nation: number,
  p: number,
  key: ResourceKey,
  amount: number,
): void {
  if (amount < 0 || !Number.isFinite(amount)) return;
  const treasury = c.nations[nation]?.treasury;
  if (!treasury) return;
  if (isVillageHolding(c, p)) {
    const prov = c.provinces[p];
    if (prov.owner !== nation) return;
    if (amount > 0) prov.villageStock[key] = (prov.villageStock[key] ?? 0) + amount;
    const at = supply.city[p] ?? -1, yard = cityAt(c, at);
    if (!yard || yard.owner !== nation || c.provinces[at]?.owner !== nation) return;
    // Unsent goods remain at their village if roads are cut or the city is
    // full. Restoring the route drains that same saved stock exactly once.
    for (const good of RESOURCE_KEYS) {
      const held = prov.villageStock[good] ?? 0;
      if (held <= 0) continue;
      if (UNCAPPED_RESOURCES.includes(good)) {
        treasury[good] += held;
        delete prov.villageStock[good];
        continue;
      }
      const put = Math.min(held, Math.max(0, cityStockCap(c, at) - yard.stock[good]));
      yard.stock[good] += put;
      if (put >= held) delete prov.villageStock[good];
      else prov.villageStock[good] = held - put;
    }
    return;
  }
  if (amount <= 0) return;
  /**
   * Coin and souls never see the inside of a warehouse.
   *
   * Ten provinces on this board have `gold` as their strategic resource and five
   * have `souls`, and routing those through a yard put a crown's coin in a shed
   * with a three-hundred-unit ceiling — which stranded most of the income of
   * every gold province on the map and is the exact opposite of the fiction this
   * file is built on. `UNCAPPED_RESOURCES` is the settlement layer's existing
   * name for the same idea: these are not things in a yard.
   */
  if (UNCAPPED_RESOURCES.includes(key)) {
    treasury[key] += amount;
    return;
  }
  const at = supply.city[p] ?? -1;
  const city = at >= 0 ? cityAt(c, at) : undefined;
  if (!city) {
    treasury[key] += amount;
    return;
  }
  const room = Math.max(0, cityStockCap(c, at) - city.stock[key]);
  const put = Math.min(room, amount);
  city.stock[key] += put;
  if (amount > put) treasury[key] += amount - put;
}

// ── The month ──────────────────────────────────────────────────────────────

/** Goods a city will not refine below, so its industry cannot eat its stores. */
export const REFINE_RESERVE = 120;
/** Raw goods a city consumes per month, per tier above the market. */
export const REFINE_PER_TIER = 12;
/** Raw goods per refined good. */
export const REFINE_RATIO = 2;

const REFINERY: ReadonlyArray<{ from: ResourceKey; to: ResourceKey }> = [
  { from: 'timber', to: 'planks' },
  { from: 'stone', to: 'runestone' },
  { from: 'iron', to: 'ingots' },
];

/**
 * A city is where goods become better goods.
 *
 * The top three rungs of the ladder want planks, runestone and ingots, and
 * nothing on the strategic map produces any of them — the province table deals
 * in timber, stone, iron, grain, gold and souls. Rather than hand the player's
 * built settlement a monopoly the AI could never share (which would cap
 * twenty-four crowns at tier six for ever), a market town and up runs its own
 * works: it turns two of the raw good into one of the refined, up to a ceiling
 * set by its tier, and never below a reserve so the industry cannot eat the
 * timber the next rung is waiting on.
 */
function refineCities(c: CampaignState): void {
  for (const city of c.cities) {
    if (isVillageHolding(c, city.at)) continue;
    const tier = tierOf(c, city.at);
    if (tier < 5) continue;
    if (c.provinces[city.at]?.owner !== city.owner || city.owner < 0) continue;
    const budget = REFINE_PER_TIER * (tier - 4);
    const cap = cityStockCap(c, city.at);
    for (const r of REFINERY) {
      const spare = Math.max(0, city.stock[r.from] - REFINE_RESERVE);
      const room = Math.max(0, cap - city.stock[r.to]) * REFINE_RATIO;
      const used = Math.min(budget, spare, room);
      if (used <= 0) continue;
      city.stock[r.from] -= used;
      city.stock[r.to] += used / REFINE_RATIO;
    }
  }
  manufactureCampaignGoods(c);
}

/**
 * A yard changing hands.
 *
 * Half of what is in it goes into the captor's treasury and the rest into the
 * mud — a sack, not an inventory transfer. It is deliberately the treasury
 * rather than the yard: an army that has just stormed a warehouse is carrying
 * what it took, and the fiction and the mechanics agree that plunder is liquid.
 */
function sackCity(c: CampaignState, city: CityState, to: number): void {
  if (to >= 0) {
    const treasury = c.nations[to]?.treasury;
    for (const k of RESOURCE_KEYS) {
      if (treasury) treasury[k] += city.stock[k] * 0.5;
      city.stock[k] = 0;
    }
  } else {
    for (const k of RESOURCE_KEYS) city.stock[k] = 0;
  }
  city.owner = to;
}

/**
 * What a capture does to a holding, called from `captureProvince`.
 *
 * Three things, and every one of them is the point. A sacked town loses a rung —
 * so ground that changes hands repeatedly never gets tall, and the tall province
 * is the one that has been *held*. Half-built works stop and the materials are
 * gone. And a warehouse is emptied into the pockets of whoever took it, which is
 * what makes a developed city the thing a war is actually about.
 */
export function onProvinceCaptured(c: CampaignState, p: number, nation: number): void {
  const prov = c.provinces[p];
  if (!prov) return;

  if (prov.worksTo > 0) {
    logLine(c, `The works at ${provinceName(p)} are abandoned. The stone stays where it lies.`);
    prov.worksTo = 0;
    prov.worksT = 0;
    prov.worksBy = -1;
  }

  const tier = tierOf(c, p);
  if (tier > MIN_TIER) {
    prov.tier = tier - 1;
    logLine(c, isVillageHolding(c, p) ? `${provinceName(p)} is sacked back to village level ${prov.tier}.`
      : `${provinceName(p)} is sacked back to a ${tierName(prov.tier).toLowerCase()}.`);
  } else {
    prov.tier = MIN_TIER;
  }

  const city = cityAt(c, p);
  if (city && city.owner !== nation) sackCity(c, city, nation);
}

/**
 * Provinces that changed hands by some route other than a storm — a province
 * ceded at a peace table, say — reconciled once a month.
 *
 * `onProvinceCaptured` is the fast path and covers everything that goes through
 * `captureProvince`; this is the net underneath it, and it exists because a
 * warehouse whose recorded owner and actual owner disagree would go on feeding
 * the crown that lost it.
 */
function reconcileCities(c: CampaignState): void {
  for (const city of c.cities) {
    const owner = c.provinces[city.at]?.owner ?? -1;
    if (owner === city.owner) continue;
    sackCity(c, city, owner);
  }

  // Only permanent city sites have warehouses. Village levels never grant one.
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner < 0) continue;
    if (isVillageHolding(c, p)) continue;
    charterCity(c, p);
  }

  /**
   * Every living crown that holds ground holds a city.
   *
   * Without this a crown that lost its seat would have nowhere for its goods to
   * go and no way to build anything ever again, which is a death spiral dressed
   * up as a rule. The court moves to whatever it has left: the tallest holding,
   * then the richest, then the lowest index — and it is free, because the
   * alternative is a crown that can never recover.
   */
  for (let n = 0; n < c.nations.length; n++) {
    if (!c.nations[n].alive) continue;
    if (c.cities.some((k) => c.provinces[k.at]?.owner === n)) continue;
    let seat = -1;
    for (let p = 0; p < c.provinces.length; p++) {
      if (c.provinces[p].owner !== n) continue;
      if (isVillageHolding(c, p)) continue;
      if (seat < 0) {
        seat = p;
        continue;
      }
      const better =
        tierOf(c, p) > tierOf(c, seat) ||
        (tierOf(c, p) === tierOf(c, seat) && provinceDef(p).wealth > provinceDef(seat).wealth);
      if (better) seat = p;
    }
    if (seat >= 0) {
      charterCity(c, seat, true);
      logLine(c, `${nationDef(c.nations[n].def).name} moves its court to ${provinceName(seat)}.`);
    }
  }
}

/** Masons at work; a rung lands when the months run out. */
function stepWorks(c: CampaignState): void {
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    if (prov.worksTo <= 0) continue;
    if (prov.owner !== prov.worksBy) {
      prov.worksTo = 0;
      prov.worksT = 0;
      prov.worksBy = -1;
      continue;
    }
    prov.worksT--;
    if (prov.worksT > 0) continue;
    prov.tier = isVillageHolding(c, p) ? Math.min(MAX_VILLAGE_LEVEL, clampTier(prov.worksTo)) : clampTier(prov.worksTo);
    prov.worksTo = 0;
    prov.worksT = 0;
    prov.worksBy = -1;
    if (isVillageHolding(c, p)) {
      const stats = villageLevelStats(villageTypeOf(c, p)!, prov.tier);
      prov.fort = Math.max(prov.fort, stats.fort);
      if (provinceDef(p).terrain === 'coast') prov.port = Math.max(prov.port, stats.port);
      logLine(c, `${provinceName(p)} reaches village level ${prov.tier}. Its preset works and watch improve.`);
      continue;
    }
    const step = tierStep(prov.tier);
    logLine(
      c,
      `${provinceName(p)} is a ${tierName(prov.tier).toLowerCase()} now.` +
        (step ? ` ${step.text}` : ''),
    );
  }
}

/**
 * The holdings' month: works, charters, industry, and the crowns deciding what
 * to build next.
 *
 * Called from `TurnPhase.Income`, after the tithes are in and after the brigands
 * have had their turn. Nothing in here touches `rngCampaign` — see
 * `developmentCouncil` for why that is a deliberate constraint rather than an
 * omission.
 */
export function stepHoldings(c: CampaignState): void {
  stepWorks(c);
  reconcileCities(c);
  refineCities(c);
  for (let n = 0; n < c.nations.length; n++) {
    if (n === c.playerNation) continue;
    if (!c.nations[n].alive) continue;
    developmentCouncil(c, n);
  }
}

// ── The crowns build too ───────────────────────────────────────────────────

/** Gold a crown keeps back for hosts before it will spend any on masons. */
export const AI_GOLD_RESERVE = 240;

/** Months between sittings, for the least and the most warlike crown. */
export const BUILD_REVIEW_FAST = 2;
export const BUILD_REVIEW_SLOW = 8;

/**
 * How often this crown's council turns to building at all.
 *
 * A militarist crown puts its money into men and reviews its masons rarely; a
 * mercantile one sits on it every other month. Rounded to a whole number of
 * months because it is a calendar, not a probability.
 */
export function buildReviewInterval(militarism: number): number {
  const m = Math.max(0, Math.min(1, militarism));
  return Math.round(BUILD_REVIEW_FAST + (BUILD_REVIEW_SLOW - BUILD_REVIEW_FAST) * m);
}

/**
 * One crown's building decision for the month.
 *
 * Exported and callable on its own, because `ai.ts` may reasonably want to fold
 * it into `nationTurn` alongside `muster` — one line, `developmentCouncil(c, n)`,
 * and this file's own loop should then be dropped. It is driven from here for
 * now so that the AI develops whether or not anybody has edited `ai.ts`: a board
 * where only the player climbs the ladder is a board the player wins by default,
 * which is the failure mode this is guarding against.
 *
 * ── Why this rolls no dice ─────────────────────────────────────────────────
 *
 * It would be natural to write the appetite as `chance(rng, …)` off
 * `w.rngCampaign`, and the first draft did. It cost a march test in
 * `campaign-depth`, and the reason it did is the reason this is now a calendar.
 * `rngCampaign` is one shared stream: bandits, battles, musters, betrothals and
 * peace offers all draw from it in turn, and every outcome any of them has ever
 * been tuned to depends on its *position* in that stream. A new consumer that
 * draws once per living crown per month does not add randomness to this system,
 * it retunes every system downstream of it — three hundred passing tests and a
 * balance harness pinned to those draws, all shifted, none of them wrong before
 * and none obviously wrong after. That is a large bill for an appetite.
 *
 * So the council sits on a schedule instead: every `buildReviewInterval` months,
 * staggered by nation index so twenty-four crowns are not all breaking ground in
 * the same month. It is deterministic, it consumes nothing, and it is the better
 * fiction — a treasury reviews its public works on a calendar. The variety that
 * matters is downstream of it anyway, in the scoring, which reads a board no two
 * crowns share.
 */
export function developmentCouncil(c: CampaignState, nation: number): void {
  const crown = c.nations[nation];
  if (!crown?.alive) return;
  if (worksCount(c, nation) >= MAX_WORKS_PER_NATION) return;
  if (crown.treasury.gold < AI_GOLD_RESERVE) return;

  const def = nationDef(crown.def);
  if ((c.turn + nation) % buildReviewInterval(def.militarism) !== 0) return;

  let best = -1;
  let bestScore = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    if (prov.owner !== nation) continue;
    if (developBlock(c, p, nation) !== '') continue;
    const cost = developCost(c, p);
    if (!cost) continue;
    // Keep the reserve intact: a crown that spends its last coin on a road
    // cannot pay its hosts, and the hosts notice within the month.
    if (crown.treasury.gold - cost.gold < AI_GOLD_RESERVE * 0.5) continue;

    const d = provinceDef(p);
    /**
     * What a rung is worth, over what it costs. Seats score higher because a
     * crown builds its capital first and everybody's capital is defensible;
     * restive and far-flung ground scores lower because the haul is dearer and
     * the yield is smaller.
     */
    const gain = (d.wealth * 1.6 + d.levy) * (tierYield(cost.to) - tierYield(cost.from));
    const seat = nationCapital(nation) === p ? 1.5 : 1;
    const score = (gain * seat * (1 - prov.unrest)) / (cost.gold * cost.haulMul);
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  if (best < 0) return;
  beginWorks(c, best, nation);
}

// ── What the world map needs to draw ───────────────────────────────────────

/** Everything the province panel wants about a holding, in one read. */
export interface HoldingReport {
  province: number;
  name: string;
  /** True when the province is held by the nation asked about. */
  held: boolean;
  owner: number;
  ownerName: string;
  tier: number;
  tierName: string;
  /** The step this rung granted, if it granted one. */
  step: TierStep | undefined;
  /** What it pays its holder each month, as things stand. */
  income: HoldingIncome;
  /**
   * The yard, per resource, and its ceiling — set only when the province is a
   * city *and* the crown asking holds it.
   *
   * Gated deliberately. What is in a rival's warehouse is exactly the
   * information that decides whether they can raise their walls before your host
   * arrives, and this realm already models what a crown can and cannot see
   * (`warnings.ts`). A panel that handed it over for free would be a scouting
   * report nobody had to scout for.
   */
  yard: Array<{ key: ResourceKey; amount: number; cap: number }> | null;
  /** Where its goods go and what a wagon train from there costs. Null if unheld. */
  supply: { city: number; cityName: string; dist: number; haulMul: number } | null;
  /** Masons currently on it. */
  works: { to: number; toName: string; months: number; by: number } | null;
  /** The next rung: what it costs, what it adds, and why it may not be ordered. */
  next: {
    tier: number;
    tierName: string;
    cost: DevelopCost;
    /** The difference the rung would make to `income`, per month. */
    gain: HoldingIncome;
    /** The step it would grant, if any. */
    step: TierStep | undefined;
    /** '' when it may be ordered right now; otherwise the reason it may not. */
    blocked: string;
  } | null;
}

/**
 * The whole panel, from one call.
 *
 * One function rather than a dozen because the world map should not have to know
 * that a tithe is scaled by unrest, that the haul is priced off the nearest city
 * and not the one the goods actually come from, or that a rung's gain is the
 * difference of two multipliers. It asks what the holding is worth; this answers.
 *
 * `nation` is who is asking — the player, normally. It decides `held` and which
 * crown's refusals `blocked` reports.
 */
export function holdingReport(c: CampaignState, p: number, nation: number): HoldingReport {
  const prov = c.provinces[p];
  const tier = tierOf(c, p);
  const city = cityAt(c, p);
  const route = prov.owner >= 0 ? supplyRouteOf(c, p) : null;

  const cost = developCost(c, p);
  let next: HoldingReport['next'] = null;
  if (cost) {
    const now = holdingIncome(c, p);
    const ratio = tierYield(cost.to) / tierYield(cost.from);
    const levyRatio = tierLevy(cost.to) / tierLevy(cost.from);
    // The tithe is a step rather than a multiple, so it is recomputed rather
    // than scaled: the fifth rung adds goods to a province that paid none.
    const after = holdingIncome(c, p, cost.to);
    next = {
      tier: cost.to,
      tierName: tierName(cost.to),
      cost,
      gain: {
        gold: now.gold * (ratio - 1),
        // The trade half rises with the ladder exactly as the tax half does, and
        // the panel should say so — otherwise a rung looks a third weaker than
        // it is to anybody who holds their own market.
        trade: now.trade * (ratio - 1),
        levy: now.levy * (levyRatio - 1),
        goods: diffGoods(now.goods, after.goods),
      },
      step: tierStep(cost.to),
      blocked: developBlock(c, p, nation),
    };
  }

  return {
    province: p,
    name: provinceName(p),
    held: prov.owner === nation,
    owner: prov.owner,
    ownerName: prov.owner >= 0 ? nationDef(c.nations[prov.owner].def).name : 'Nobody',
    tier,
    tierName: tierName(tier),
    step: tierStep(tier),
    income: holdingIncome(c, p),
    yard:
      city && prov.owner === nation
        ? RESOURCE_KEYS.map((k) => ({
            key: k,
            amount: city.stock[k],
            cap: cityStockCap(c, p),
          })).filter((e) => e.amount > 0.01 || DEV_GOODS.some((g) => g.key === e.key))
        : null,
    supply:
      route && route.city >= 0
        ? {
            city: route.city,
            cityName: provinceName(route.city),
            dist: route.dist,
            haulMul: haulMultiplier(c, route),
          }
        : null,
    works:
      prov.worksTo > 0
        ? {
            to: prov.worksTo,
            toName: tierName(prov.worksTo),
            months: prov.worksT,
            by: prov.worksBy,
          }
        : null,
    next,
  };
}

function diffGoods(
  before: Array<{ key: ResourceKey; amount: number }>,
  after: Array<{ key: ResourceKey; amount: number }>,
): Array<{ key: ResourceKey; amount: number }> {
  const out: Array<{ key: ResourceKey; amount: number }> = [];
  for (const a of after) {
    const b = before.find((x) => x.key === a.key);
    const delta = a.amount - (b?.amount ?? 0);
    if (delta > 0.001) out.push({ key: a.key, amount: delta });
  }
  return out;
}

/** Every holding this crown has, tallest first — the "my realm" list. */
export function holdingsOfNation(c: CampaignState, nation: number): HoldingReport[] {
  const out: HoldingReport[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    out.push(holdingReport(c, p, nation));
  }
  out.sort((a, b) => b.tier - a.tier || b.income.gold - a.income.gold || a.province - b.province);
  return out;
}
